import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Stack } from "@mui/material";
import { Pause } from "@mui/icons-material";
import { RealTimeItemMonitoring } from "@common/components/ListDetailViews/common/RealTimeStatus/type";
import { Device } from "@services/devices/types";
import { getIsDevicePaused } from "@common/utils/devices/getIsDevicePaused";
import { displayDateTime } from "@common/utils/localTime.utils";
import { PauseMonitoringStatus } from "@services/rooms/types";
import { DeviceCardMonitoringStatusItem } from "./DeviceCardMonitoringStatusItem";

interface DeviceCardMonitoringStatusProps {
  device: Device;
  items: RealTimeItemMonitoring[];
  previousPage?: string;
}

export const getPauseTooltip = (
  pauseStatus: PauseMonitoringStatus | undefined,
  t: TFunction
) => {
  if (pauseStatus?.pausedUntil) {
    return t("Paused until : {{ date }}", {
      date: displayDateTime(pauseStatus?.pausedUntil),
    });
  }

  if (pauseStatus?.outOfScheduleUntil) {
    return `${t("Paused until : {{ date }}", {
      date: displayDateTime(pauseStatus?.outOfScheduleUntil),
    })} - out of schedule`;
  }

  return t("Paused");
};

export const DeviceCardMonitoringStatus = ({
  items,
  device,
  previousPage,
}: DeviceCardMonitoringStatusProps) => {
  const { t } = useTranslation();
  const linkTo = `/assets/devices/${device.id}`;
  const isDevicePaused = getIsDevicePaused(device);

  if (isDevicePaused) {
    const tooltip = getPauseTooltip(device.status?.pauseMonitoringStatus, t);

    const pausedItem: RealTimeItemMonitoring = {
      type: "indication",
      icon: <Pause />,
      status: "unknown",
      tooltip,
    };

    return (
      <DeviceCardMonitoringStatusItem
        linkTo={linkTo}
        previousPage={previousPage}
        item={pausedItem}
        isDevicePaused
      />
    );
  }

  return (
    <Stack direction={"row"} sx={{ gap: 0.5 }}>
      {items.map((item, index) => {
        return (
          <DeviceCardMonitoringStatusItem
            key={index}
            linkTo={linkTo}
            previousPage={previousPage}
            item={item}
          />
        );
      })}
    </Stack>
  );
};
